import * as React from 'react';
import styled, { css } from 'styled-components';

import { IDataset } from '../../../types/IDataset';
import { IDatagroup } from '../../../types/IDatagroup';
import { AquaMenuGroup } from './AquaMenuGroup';
import { AquaTheme as T } from '../AquaTheme';
import { Config } from '../../../Config';

// Gap between the panel and the edges of the map, in pixels.
const MARGIN = 12;

interface IProps {
  /** @ignore */
  className?: string;
  /** Is the menu currently open? */
  open: boolean;
  /** Datasets to show in menu. */
  datasets: IDataset[];
  /** Datagroups to show in menu. */
  datagroups: IDatagroup[];
  /** Fired when a menu item is clicked. */
  onToggleLayer: (key: string) => void;
  /** Fired when menu is toggled. */
  onToggle: () => void;
}

/**
 * Layer menu in the AquaUI style: a floating, rounded panel with AquaUI's
 * blue gradient, and an AquaUI ToggleButton to open it.
 */
const AquaMenuBase = (props: IProps) => {
  const handleToggle = (e: React.MouseEvent) => {
    // Must stop propagation for Main not to close the menu again:
    e.stopPropagation();
    props.onToggle();
  }

  const activeCount = props.datasets.filter(d => d.active).length;

  return (
    <div className={props.className}>
      <OpenButton type="button" title="Map layers" aria-label="Open map layers" aria-expanded={props.open} onClick={handleToggle}>
        <svg><use xlinkHref={"sprites.svg#layers"}></use></svg>
        {activeCount > 0 && <Badge>{activeCount}</Badge>}
      </OpenButton>
      {/* Clicks inside the panel must not close the menu. */}
      <Panel role="region" aria-label="Map layers" onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderIcon>
            <svg><use xlinkHref={"sprites.svg#layers"}></use></svg>
          </HeaderIcon>
          <HeaderText>
            <Title>Map layers</Title>
            <Subtitle>{activeCount} of {props.datasets.length} active</Subtitle>
          </HeaderText>
          <CloseButton type="button" title="Close" aria-label="Close map layers" onClick={handleToggle}>
            <svg><use xlinkHref={"sprites.svg#times"}></use></svg>
          </CloseButton>
        </Header>
        <Scroller>
          {props.datagroups.map((group, idx) =>
            <AquaMenuGroup key={idx} group={group} datasets={props.datasets.filter(d => d.group == group.key)} onClick={props.onToggleLayer}/>)}
        </Scroller>
      </Panel>
    </div>
  )
}

const Badge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 9px;
  background: ${T.colors.accent};
  color: ${T.colors.neutral[100]};
  font: ${T.font.labelCaps};
  line-height: 18px;
  text-align: center;
  box-shadow: ${T.shadows.medium};
`

// Matches AquaUI's ToggleButton.
const OpenButton = styled.button`
  position: absolute;
  right: 100%;
  top: ${MARGIN}px;
  margin-right: ${MARGIN}px;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: ${T.radius.normal}px;
  background-color: ${T.colors.primary[1]};
  box-shadow: ${T.shadows.large};
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color ease-in-out ${T.duration}ms,
              box-shadow ease-in-out ${T.duration}ms,
              opacity ease-in-out 150ms;
  svg {
    width: 22px;
    height: 22px;
    fill: ${T.colors.neutral[100]};
  }
  &:hover {
    box-shadow: ${T.shadows.medium};
  }
  &:active {
    background-color: ${T.colors.primary[4]};
    box-shadow: ${T.shadows.inner}, ${T.shadows.medium};
  }
  &:focus-visible {
    outline: solid 2px ${T.colors.neutral[100]};
  }
`

const Panel = styled.div`
  position: absolute;
  left: ${MARGIN}px;
  right: ${MARGIN}px;
  top: ${MARGIN}px;
  bottom: ${MARGIN}px;
  display: flex;
  flex-direction: column;
  background-image: ${T.gradient};
  border-radius: ${T.radius.strong}px;
  box-shadow: ${T.shadows.large};
  overflow: hidden;
  color: ${T.colors.neutral[100]};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 16px 16px;
  border-bottom: solid 1px rgba(255,255,255,0.1);
`

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${T.radius.normal}px;
  background: ${T.colors.primary[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  svg {
    width: 20px;
    height: 20px;
    fill: ${T.colors.neutral[100]};
  }
`

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled.div`
  font: ${T.font.headingSmall};
`

const Subtitle = styled.div`
  font: ${T.font.bodySmall};
  color: ${T.colors.neutral[80]};
`

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  flex: none;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0,0,0,0.2);
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color ease-in-out ${T.duration}ms;
  svg {
    width: 12px;
    height: 12px;
    fill: ${T.colors.neutral[100]};
  }
  &:hover {
    background: ${T.colors.primary[5]};
  }
  &:focus-visible {
    outline: solid 2px ${T.colors.neutral[100]};
  }
`

const Scroller = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 12px 16px 12px;
  scrollbar-width: thin;
  scrollbar-color: ${T.colors.primary[2]} transparent;
`

const AquaMenu = styled(AquaMenuBase)`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1000;
  box-sizing: border-box;
  user-select: none;
  transition: right ease-out 200ms;
  // When closed, the menu sits entirely beyond the right edge, leaving only
  // the open button visible.
  // Small screen:
  width: calc(100% - 64px);
  right: ${p => p.open ? '0' : 'calc(64px - 100%)'};
  // Large screen:
  @media (min-width: ${Config.UI.large_screen}px) {
    width: 400px;
    right: ${p => p.open ? '0' : '-400px'};
  }

  // A closed panel is hidden once it has slid away, so that its buttons can't
  // take focus (focusing them would scroll the map container sideways).
  ${Panel} {
    visibility: ${p => p.open ? 'visible' : 'hidden'};
    transition: visibility 0s linear ${p => p.open ? 0 : 200}ms;
  }

  ${OpenButton} {
    ${p => p.open && css`
      opacity: 0;
      pointer-events: none;
    `}
  }
`

export { AquaMenu }
